import type { CSSProperties } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { bulletColor, bulletSvg, isIconBullet } from 'Guide/step_bullets';
import type { StepLine } from './types';
import { useWikiHtml } from './useWikiHtml';

/** The wiki-HTML text lines of a step, with per-line bullet color, indentation,
 * and the red caution callout. Shared by the image and video step layouts. */
export function StepLines({ lines }: { lines: StepLine[] }) {
   // Step text is wiki HTML: force link targets (and hydrate any video embed).
   const ref = useWikiHtml<HTMLOListElement>(lines);
   return (
      <ol ref={ref} className="m-0 list-none p-0 [&_b]:font-semibold [&_strong]:font-semibold">
         {lines.map((line, i) => (
            <StepLineItem key={line.lineid ?? i} line={line} />
         ))}
      </ol>
   );
}

function StepLineItem({ line }: { line: StepLine }) {
   const isIcon = isIconBullet(line.bullet);
   const isCaution = line.bullet === 'icon_caution';
   // One palette (Guide/step_bullets) so the reader can't drift from the editor
   // and the published guide.
   const color = bulletColor(line.bullet);

   const style: CSSProperties = {};
   if (line.level > 1) {
      style.marginLeft = `${line.level * 1.5}rem`;
   }

   return (
      <li
         className={cn(
            'relative mb-3 pl-6 text-base leading-[1.5]',
            // The system's critical-alert fill, not a colored edge: DESIGN.md
            // rules out side-stripe callouts, and marker red on white missed 4.5:1.
            isCaution
               ? 'rounded-lg bg-red-100 py-2.5 pl-10 pr-4 font-semibold text-red-900'
               : 'text-gray-950',
            line.level > 0 && 'ml-6'
         )}
         style={style}
      >
         {isIcon ? (
            <span
               aria-hidden
               // Caution sits inside the callout's left padding, note/reminder
               // where a color dot would. fill-current colors the glyph (the
               // bundled SVGs carry no fill); reminder keeps its boxed outline,
               // matching guide-view and BulletSelector.
               className={cn(
                  'absolute flex size-4 items-center justify-center [&>svg]:size-full [&>svg]:fill-current',
                  isCaution ? 'left-3 top-3' : 'left-0 top-[3px]',
                  line.bullet === 'icon_reminder' &&
                     'rounded-sm border-[1.5px] border-current p-0.5'
               )}
               // The glyph keeps the marker color so it matches the marker the
               // author drew on the step image; only the copy is darkened.
               style={{ color }}
               // Trusted inline SVGs from bullet_icons.js, not user content.
               dangerouslySetInnerHTML={{ __html: bulletSvg(line.bullet) }}
            />
         ) : (
            <span
               aria-hidden
               className="absolute left-0 top-2 size-2 rounded-full"
               style={{ backgroundColor: color }}
            />
         )}
         {/* Rendered by the guide API's wiki renderer (server-sanitized HTML). */}
         <span dangerouslySetInnerHTML={{ __html: line.text_rendered }} />
      </li>
   );
}
